// src/components/ProjectBuilder/TaskSummaryCards.tsx
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import { Warning, CalendarToday, Sync, Assignment } from '@mui/icons-material';
import { TaskFilters } from '../../services/taskApi';

export interface TaskSummaryCardsProps {
  summary: {
    totalTasks: number;
    pastDueCount: number;
    upcomingCount: number;
    inProgressCount: number;
    completeCount: number;
  };
  onFilterChange: (filter: Partial<TaskFilters>) => void;
  activeFilters: TaskFilters;
}

export default function TaskSummaryCards({ summary, onFilterChange, activeFilters }: TaskSummaryCardsProps) {
  const isPastDueActive = activeFilters.pastDueOnly === true;
  const isUpcomingActive = activeFilters.upcomingDays !== undefined;
  const isInProgressActive = activeFilters.status === 'IN_PROGRESS';
  const isTotalActive = !isPastDueActive && !isUpcomingActive && !isInProgressActive;

  const handleCardClick = (filterType: 'total' | 'pastDue' | 'upcoming' | 'inProgress') => {
    switch (filterType) {
      case 'total':
        onFilterChange({ pastDueOnly: undefined, upcomingDays: undefined, status: undefined });
        break;
      case 'pastDue':
        onFilterChange({ pastDueOnly: true, upcomingDays: undefined, status: undefined });
        break;
      case 'upcoming':
        onFilterChange({ pastDueOnly: undefined, upcomingDays: 7, status: undefined });
        break;
      case 'inProgress':
        onFilterChange({ pastDueOnly: undefined, upcomingDays: undefined, status: 'IN_PROGRESS' });
        break;
    }
  };

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {/* Total Tasks Card */}
      <Grid item xs={6} sm={3}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isTotalActive ? 'primary.light' : 'background.paper',
            border: isTotalActive ? 2 : 1,
            borderColor: isTotalActive ? 'primary.main' : 'divider',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('total')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {summary.totalTasks}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Assignment fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Total Tasks
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Past Due Card */}
      <Grid item xs={6} sm={3}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isPastDueActive ? 'error.light' : 'background.paper',
            border: isPastDueActive ? 2 : 1,
            borderColor: isPastDueActive ? 'error.main' : 'divider',
            '&:hover': {
              bgcolor: 'error.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('pastDue')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'error.main' }}>
              {summary.pastDueCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Warning fontSize="small" color="error" />
              <Typography variant="body2" color="text.secondary">
                Past Due
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Upcoming Card */}
      <Grid item xs={6} sm={3}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isUpcomingActive ? 'warning.light' : 'background.paper',
            border: isUpcomingActive ? 2 : 1,
            borderColor: isUpcomingActive ? 'warning.main' : 'divider',
            '&:hover': {
              bgcolor: 'warning.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('upcoming')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'warning.main' }}>
              {summary.upcomingCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <CalendarToday fontSize="small" color="warning" />
              <Typography variant="body2" color="text.secondary">
                Upcoming
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* In Progress Card */}
      <Grid item xs={6} sm={3}>
        <Card
          sx={{
            cursor: 'pointer',
            bgcolor: isInProgressActive ? 'success.light' : 'background.paper',
            border: isInProgressActive ? 2 : 1,
            borderColor: isInProgressActive ? 'success.main' : 'divider',
            '&:hover': {
              bgcolor: 'success.light',
              transform: 'translateY(-2px)',
              boxShadow: 2,
            },
            transition: 'all 0.2s',
          }}
          onClick={() => handleCardClick('inProgress')}
        >
          <CardContent sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: 'success.main' }}>
              {summary.inProgressCount}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <Sync fontSize="small" color="success" />
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

